import { MatchSuggestion } from '../../../models/bill-import.models';
import { MIN_DISPLAY_SCORE } from '../../../utils/match-score.util';
import { FieldError } from '../validation';

/**
 * Shared builders for the Express Import pause-reason banner. Every entity
 * step (LEN, SA, Meter) produces the same shape of blocker — an ambiguous
 * match, a low-confidence match, or a row with missing required fields — so
 * the phrasing is centralized here to keep copy consistent and grammar
 * correct. See guides/ai-onboarding/FAST-TRACK.md for how these feed the
 * `.jen-zen-pause-banner` and per-row `.needs-attention` outlines.
 */

/** Singular/plural noun for an entity kind, used to build "N more X" phrases. */
export interface EntityKind {
  /** Singular — e.g. "legal entity", "service address", "meter" */
  singular: string;
  /** Plural — e.g. "legal entities", "service addresses", "meters" */
  plural: string;
}

export const LEN_KIND: EntityKind = { singular: 'legal entity', plural: 'legal entities' };
export const SA_KIND: EntityKind = { singular: 'service address', plural: 'service addresses' };
export const METER_KIND: EntityKind = { singular: 'meter', plural: 'meters' };

/** One row's pause-reason, tied to its groupId so the UI can highlight it. */
export interface PauseBlocker {
  groupId: string;
  fragment: string;
}

/**
 * True when the match response has at least one candidate the user would see
 * in the dropdown (either the best match or an alternative with a score at or
 * above MIN_DISPLAY_SCORE). Used by the analyzer to decide whether a row is
 * genuinely ambiguous — a low-confidence match with no displayable options is
 * a no-op choice (user just creates new), not a blocker.
 */
export function hasDisplayableOptions(
  match: MatchSuggestion | null,
  alternatives: MatchSuggestion[] | undefined,
): boolean {
  if (match != null && match.score >= MIN_DISPLAY_SCORE) return true;
  if (alternatives) {
    for (const alt of alternatives) {
      if (alt.score >= MIN_DISPLAY_SCORE) return true;
    }
  }
  return false;
}

/**
 * Summary of a weak-but-displayable match that the analyzer didn't force a
 * pause on, but the user should still see on the summary step so they don't
 * miss a potentially relevant candidate. The match API is defensive — it
 * checks for accidental duplicates at every hierarchy level — so these
 * signals are valuable even when the cascade says the row is definitely new.
 * See guides/ai-onboarding/FAST-TRACK.md § Summary Warnings.
 */
export interface UnappliedMatchWarning {
  entityKind: 'customer' | 'legal entity' | 'service address' | 'meter';
  /**
   * Human-readable label for the row being created. For customer-level
   * warnings when the user is creating a new customer, this is left blank
   * (the warning copy already says "the customer you are creating").
   */
  entityLabel: string;
  bestMatchLabel: string;
  bestMatchScorePct: number;
}

/**
 * Render a summary-step bullet for an unapplied match. Centralized here so
 * the customer-level phrasing (which doesn't have a per-row label) stays
 * consistent with the LEN/SA/Meter phrasing.
 */
export function formatUnappliedMatchWarning(w: UnappliedMatchWarning): string {
  if (w.entityKind !== 'customer' && !w.entityLabel) {
    // Non-customer kinds are expected to carry a human-readable label
    // (LEN name, SA address, meter UAN). An empty label almost certainly
    // means a missing labelFor lookup in the analyzer — surface it loudly
    // rather than rendering "legal entity \"\"" in the banner.
    throw new Error(`formatUnappliedMatchWarning: ${w.entityKind} warning requires a non-empty entityLabel`);
  }
  const subject = w.entityKind === 'customer'
    ? `the customer you are creating`
    : `${w.entityKind} "${w.entityLabel}"`;
  const stepLink = w.entityKind === 'customer' ? 'customer step' : `${w.entityKind} step`;
  return `Possible match for ${subject}: "${w.bestMatchLabel}" at ${w.bestMatchScorePct}%. Creating new — open the ${stepLink} to link instead if this looks right.`;
}

/**
 * Verify-or-link action appended once to a match-ambiguity pause reason (see
 * {@link joinPauseReason}). Kept separate from the per-row fact fragments so it
 * reads once at the end of the banner rather than repeating per blocker.
 */
export const VERIFY_OR_LINK_ACTION_SINGULAR = `Please verify it's a new entity, or link it to an existing one`;
export const VERIFY_OR_LINK_ACTION_PLURAL = `Please verify each is a new entity, or link it to an existing one`;

/**
 * Build the per-row "ambiguous match" fact fragment used when fast-track stops
 * on an entity with a close-but-not-certain match. States which new entity is
 * involved and what existing entity it resembles; the verify-or-link ask is
 * appended once by {@link joinPauseReason} so it isn't repeated per row.
 *
 * PRECONDITION: `match` is non-null. The caller only builds a blocker for a row
 * whose match is displayable (see {@link hasDisplayableOptions}), and the match
 * API never returns a null match alongside alternatives — when nothing clears
 * the floor, both are empty. A null match here is a programmer error and throws;
 * `analyzeFastTrack`'s try/catch surfaces it as the standard "Fast-track
 * analysis failed" banner rather than producing a nonsense fragment.
 */
export function buildAmbiguousMatchFragment(
  entityLabel: string,
  match: MatchSuggestion | null,
): string {
  if (match == null) {
    throw new Error(`buildAmbiguousMatchFragment called without a match (entity: "${entityLabel}")`);
  }
  const pct = Math.round(match.score * 100);
  return `Your new entity "${entityLabel}" matches an existing entity, "${match.label}", at ${pct}%`;
}

/**
 * Join per-entity blocker fragments into a single pause-reason string.
 * Inlines up to two fragments verbatim, then collapses the rest into
 * "and N more X need[s] review" with correct subject-verb agreement.
 *
 * When `verifyActionCount` is > 0 (match-ambiguity blockers from
 * {@link buildAmbiguousMatchFragment}), the verify-or-link ask is appended
 * once at the end. The count is the number of ambiguous-match blockers, NOT
 * `blockers.length`: a meter step can mix match-ambiguity and duplicate-bill
 * blockers, so the singular/plural of the ask must track the ambiguous count
 * alone (one ambiguous meter alongside a duplicate must still read "verify
 * it's", not "verify each"). The banner template supplies the final period,
 * so the returned string is unterminated.
 */
export function joinPauseReason(
  blockers: PauseBlocker[],
  kind: EntityKind,
  verifyActionCount = 0,
): string {
  let facts: string;
  if (blockers.length === 0) facts = `Some ${kind.plural} need review`;
  else if (blockers.length === 1) facts = blockers[0].fragment;
  else if (blockers.length === 2) facts = `${blockers[0].fragment}; ${blockers[1].fragment}`;
  else {
    const extra = blockers.length - 2;
    const noun = extra === 1 ? kind.singular : kind.plural;
    const verb = extra === 1 ? 'needs' : 'need';
    facts = `${blockers[0].fragment}; ${blockers[1].fragment}; and ${extra} more ${noun} ${verb} review`;
  }
  if (verifyActionCount <= 0) return facts;
  const action = verifyActionCount > 1 ? VERIFY_OR_LINK_ACTION_PLURAL : VERIFY_OR_LINK_ACTION_SINGULAR;
  return `${facts}. ${action}`;
}

/**
 * Build a pause-reason string for a step blocked by missing required fields.
 * Groups missing fields by entity so the banner reads as
 * "Acme LLC is missing Billing ZIP and Billing City".
 *
 * PRECONDITION: caller has already checked `validation.hasBlockers`, so
 * `errors` is non-empty. An empty array is a programmer error and throws;
 * `analyzeFastTrack`'s try/catch will surface it rather than silently
 * producing generic "Required field(s) missing" copy with no entity context.
 */
export function buildFieldPauseReason(errors: FieldError[], kind: EntityKind): string {
  if (errors.length === 0) {
    throw new Error(`buildFieldPauseReason called with no errors (kind: ${kind.singular})`);
  }
  const byEntity = new Map<string, { label: string; fields: string[] }>();
  for (const err of errors) {
    const entry = byEntity.get(err.entityId);
    if (entry) {
      if (!entry.fields.includes(err.label)) entry.fields.push(err.label);
    } else {
      byEntity.set(err.entityId, { label: err.entityLabel, fields: [err.label] });
    }
  }
  const parts = Array.from(byEntity.values()).map(
    e => `${e.label} is missing ${formatList(e.fields)}`,
  );
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]}; ${parts[1]}`;
  const extra = parts.length - 2;
  const noun = extra === 1 ? kind.singular : kind.plural;
  const verb = extra === 1 ? 'needs' : 'need';
  return `${parts[0]}; ${parts[1]}; and ${extra} more ${noun} ${verb} required fields`;
}

/** Format an array as a natural-language list: "A", "A and B", "A, B, and C". */
export function formatList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
